import { useMemo } from "react";

import { type ChartDataset, type ChartOptions } from "chart.js";
import { type PartialEventContext } from "chartjs-plugin-annotation";
import { Line } from "react-chartjs-2";

import { useUser } from "@components/user-context";
import { useModelInsightsKpiTimePeriod } from "@features/mmm/hooks/mmm-url-state";
import { efficiencyKpiManager } from "@features/mmm/utils/efficiency-kpi-manager";
import { MetricFormatters } from "@north-beam/nb-client-side";
import { IconButton } from "@shared/buttons";

import {
  type Kpi,
  type ModelMetadataKpiFormula,
} from "../../explore-budgets/client-setting/types";
import { useChannelComparisonModal } from "../hooks/use-channel-comparison-modal";
import { makeFont } from "../model-fit-chart/utils/annotation-helpers";
import { curateHistoricalDataset } from "../model-fit-chart/utils/export-to-csv";

import { ROAS_ID } from "./incremental-roas-chart/const";
import {
  findClosestIndexInArrayForValue,
  type HistoricalContributionsData,
} from "./incremental-spend-chart/incremental-spend-chart";
import { medianWeeklySpendAnnotation } from "./incremental-spend-chart/plugins/median-weekly-spend-annotation";

interface Props {
  selectedKpi: ModelMetadataKpiFormula;
  historicalContributionsData: HistoricalContributionsData;
  costCurvesAllTimeData: any;
  columnName: string;
}

export const KpiMiniChart = ({
  selectedKpi,
  historicalContributionsData,
  costCurvesAllTimeData,
  columnName,
}: Props) => {
  const { user } = useUser();
  const { openModalWithChannel } = useChannelComparisonModal();
  const [dateRange] = useModelInsightsKpiTimePeriod();
  const { efficiencyKpiType, efficiencyKpiCalculator, efficiencyKpiFormatter } =
    efficiencyKpiManager(selectedKpi as Kpi, user);

  const { data } = useMemo(() => {
    const costCurves = costCurvesAllTimeData[columnName] ?? [];
    const labels: number[] = [];
    const derivativeData: number[] = [];
    const kpiData: number[] = [];

    costCurves.forEach((row: any) => {
      const kpiValue = row.kpiData[selectedKpi.kpi_name];
      labels.push(row.spend);
      kpiData.push(kpiValue);
      derivativeData.push(efficiencyKpiCalculator(row.spend, kpiValue));
    });

    return {
      data: {
        labels,
        datasets: [
          {
            yAxisID: ROAS_ID,
            xAxisID: "x",
            tension: 0.4,
            backgroundColor: "#7081FF",
            borderColor: "#7081FF",
            pointRadius: 0,
            borderWidth: 2,
            data: derivativeData,
            animation: false,
          } as ChartDataset<"line", number[]>,
        ],
      },
    };
  }, [costCurvesAllTimeData, selectedKpi, columnName, efficiencyKpiCalculator]);

  const medianSpendIndex = useMemo(() => {
    let trueMedianSpendForHistoricalPeriod = 0;
    const historicalData = historicalContributionsData[columnName] ?? [];
    const curatedRows = curateHistoricalDataset(historicalData, dateRange);
    const allSpends = curatedRows.map((data: any) => data.spend);
    allSpends.sort((a: any, b: any) => a - b);
    if (allSpends.length % 2 === 0) {
      const idx1 = allSpends.length / 2;
      const idx2 = idx1 - 1;
      trueMedianSpendForHistoricalPeriod =
        (allSpends[idx1] + allSpends[idx2]) / 2;
    } else {
      trueMedianSpendForHistoricalPeriod =
        allSpends[Math.floor(allSpends.length / 2)];
    }

    return findClosestIndexInArrayForValue(
      data.labels,
      trueMedianSpendForHistoricalPeriod,
    );
  }, [data.labels, historicalContributionsData, columnName, dateRange]);

  const chartOptions = useMemo<ChartOptions<"line">>(() => {
    const maxXAxis = data.labels[data.labels.length - 1];

    return {
      maintainAspectRatio: false,
      responsive: true,
      layout: {
        padding: { left: 10, bottom: 6 },
      },
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false },
        annotation: {
          annotations: {
            ...medianWeeklySpendAnnotation({
              dateRange,
              medianSpendIndex,
              medianSpendLabelOptions: {
                content: "MEDIAN",
                font: makeFont({ size: 7 }),
                xAdjust: -2,
                yValue: 0,
                yAdjust: -16,
              },
            }),
          },
        },
      },
      scales: {
        x: {
          axis: "x",
          grid: {
            drawBorder: false,
            drawTicks: false,
            drawOnChartArea: false,
          },
          min: 0,
          max: maxXAxis,
          beginAtZero: true,
          ticks: {
            maxTicksLimit: 4,
            stepSize: 1,
            maxRotation: 0,
            font: { size: 7 },
            callback: (index) =>
              MetricFormatters.currency(
                Number(data.labels[Number(index)]),
                user.currencies.currentCurrency,
                { precision: "approximate" },
              ),
          },
        },
        [ROAS_ID]: {
          display: true,
          axis: "y",
          beginAtZero: true,
          grid: {
            drawTicks: true,
            drawBorder: false,
            drawOnChartArea: true,
          },
          ticks: {
            precision: 1,
            maxTicksLimit: 4,
            font: { size: 7 },
            callback: (val) => efficiencyKpiFormatter(Number(val)),
          },
        },
      },
    };
  }, [
    data,
    user.currencies.currentCurrency,
    medianSpendIndex,
    efficiencyKpiFormatter,
    dateRange,
  ]);

  return (
    <div className="flex flex-col items-end justify-between">
      <div className="relative h-full w-full border-l border-b border-v2-secondary mt-4 mb-7">
        <div className="absolute top-0 right-0 h-full w-full">
          <Line options={chartOptions} data={data} />
        </div>
        <div className="bg-v2-primary px-1 -rotate-90 translate-x-[-50%] translate-y-[250%] text-2xs font-semibold absolute">
          i{efficiencyKpiType}
        </div>
        <div className="bg-v2-primary px-1 bottom-0 translate-y-1/2 left-1/2 -translate-x-1/2 text-2xs font-semibold absolute">
          SPEND
        </div>
      </div>
      <IconButton
        className="text-v2-action font-semibold flex items-center mb-1 bg-v2-primary border-v2-secondary"
        variant="secondary"
        leftIcon={<i className="fa-regular fa-chart-line h-4 w-4 mr-2" />}
        onClick={(e) => {
          e.stopPropagation();
          openModalWithChannel(columnName);
        }}
      >
        VIEW GRAPHS
      </IconButton>
    </div>
  );
};
